import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  alpha,
  Grid, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Paper, 
  IconButton, 
  CircularProgress, 
  Alert, 
  Snackbar, 
  Tooltip, 
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Tabs,
  Tab
} from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, 
  PlusCircle, 
  XCircle, 
  ChevronDown, 
  ChevronRight, 
  PenTool, 
  BookOpen, 
  LayoutGrid, 
  ListOrdered, 
  CheckSquare,
  TestTubeDiagonal,
  ListFilter,
  X,
  Blocks,
  Info,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Copy
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import LoadingOverlay from './LoadingOverlay';
import PageHeader from './PageHeader';

const isValidInput = (input) => {
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /data:/i,
    /vbscript:/i,
    /on\w+=/i,  // onerror=, onload=, etc.
    /\{\{.*\}\}/,  // template injection
    /\$\{.*\}/,   // template literal injection
    /<iframe/i,
    /<img/i,
    /eval\(/i,
    /setTimeout/i,
    /setInterval/i,
    /new\s+Function/i
  ];

  return !dangerousPatterns.some(pattern => pattern.test(input));
};

const sanitizeInput = (input) => {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .replace(/\0/g, '')
    .replace(/[\x00-\x1F\x7F-\x9F]/g, '');
};


const PROMPT_BLOCKS = {
  writingStyle: {
    title: "Writing Style",
    icon: PenTool,
    blocks: [
      { 
        id: 'tone-friendly', 
        category: 'tone',
        label: 'Friendly Tone',
        template: 'Use a friendly and approachable tone throughout the content.'
      },
      { 
        id: 'tone-professional', 
        category: 'tone',
        label: 'Professional Tone',
        template: 'Maintain a professional and authoritative tone.'
      },
      { 
        id: 'tone-academic', 
        category: 'tone',
        label: 'Academic Tone',
        template: 'Employ an academic writing style with formal language.'
      },
      { 
        id: 'language-simple', 
        category: 'language',
        label: 'Simple Language',
        template: 'Use simple, everyday language accessible to general readers.'
      },
      { 
        id: 'language-technical', 
        category: 'language',
        label: 'Technical Language',
        template: 'Include technical terminology appropriate for industry experts.'
      }
    ]
  },
  introduction: {
    title: "Introduction Styles",
    icon: BookOpen,
    blocks: [
      {
        id: 'intro-balanced',
        category: 'introduction',
        label: 'Balanced Introduction',
        template: 'Start with a balanced introduction combining context and engaging hooks.'
      },
      {
        id: 'intro-direct',
        category: 'introduction',
        label: 'Direct Introduction',
        template: 'Begin with a direct, to-the-point introduction.'
      },
      {
        id: 'intro-story',
        category: 'introduction',
        label: 'Story-based Introduction',
        template: 'Open with a relevant anecdote or story.'
      },
      {
        id: 'intro-problem',
        category: 'introduction',
        label: 'Problem-focused Introduction',
        template: 'Start by presenting the main problem or challenge.'
      }
    ]
  },
  contentStructure: {
    title: "Content Structure",
    icon: LayoutGrid,
    blocks: [
      {
        id: 'structure-seo',
        category: 'headings',
        label: 'SEO Question Headings',
        template: 'Format main headings as SEO-friendly questions.'
      },
      {
        id: 'structure-howto',
        category: 'headings',
        label: 'How-To Format',
        template: 'Structure content as a step-by-step guide.'
      },
      {
        id: 'paragraphs-short',
        category: 'paragraphs',
        label: 'Short Paragraphs',
        template: 'Keep paragraphs concise (2-3 sentences).'
      },
      {
        id: 'paragraphs-detailed',
        category: 'paragraphs',
        label: 'Detailed Paragraphs',
        template: 'Use longer, more detailed paragraphs (4-6 sentences).'
      }
    ]
  },
  seoFeatures: {
    title: "SEO Features",
    icon: ListFilter,
    blocks: [
      {
        id: 'seo-featured',
        category: 'seo-featured',
        label: 'Featured Snippets',
        template: 'Include a featured snippet near the start of the article formatted in bold text.'
      },
      {
        id: 'seo-keywords',
        category: 'seo-keywords',
        label: 'SEO Keywords',
        template: 'Naturally incorporate target keywords throughout the content.'
      },
      {
        id: 'seo-H2-headings',
        category: 'seo-H2-headings',
        label: 'Include H2 Heading Structure',
        template: 'Use H2 headings for main sections'
      },
      {
        id: 'seo-H3-headings',
        category: 'seo-H3-headings',
        label: 'Include H3 Heading Structure',
        template: 'Use H3 headings for subsections.'
      }
    ]
  },
  additionalElements: {
    title: "Additional Elements",
    icon: ListOrdered,
    blocks: [
      {
        id: 'element-lists',
        category: 'formatting',
        label: 'Bullet Points & Lists',
        template: 'Include bullet points and numbered lists where appropriate.'
      },
      {
        id: 'element-examples',
        category: 'content',
        label: 'Examples',
        template: 'Include practical examples to illustrate key points.'
      },
      {
        id: 'element-faq',
        category: 'sections',
        label: 'FAQ Section',
        template: 'Add a FAQ section with 3-5 relevant questions and answers.'
      },
      {
        id: 'element-callouts',
        category: 'formatting',
        label: 'Key Callouts',
        template: 'Use callout boxes to highlight important information.'
      }
    ]
  },
  conclusion: {
    title: "Conclusion Styles",
    icon: CheckSquare,
    blocks: [
      {
        id: 'conclusion-summary',
        category: 'conclusion',
        label: 'Summary Conclusion',
        template: 'End with a comprehensive summary of key points.'
      },
      {
        id: 'conclusion-action',
        category: 'conclusion',
        label: 'Call-to-Action',
        template: 'Conclude with a clear call-to-action.'
      },
      {
        id: 'conclusion-questions',
        category: 'conclusion',
        label: 'Open-ended Questions',
        template: 'End with thought-provoking questions for further consideration.'
      }
    ]
  }
};

// Add the HelpDialog component definition
const HelpDialog = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HelpCircle size={24} /> How to Use the Guided Prompt Builder
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <X size={20} />
      </IconButton>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Typography gutterBottom variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Welcome to the Guided Prompt Builder!
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          This tool helps you create powerful, structured AI prompts step-by-step. By selecting pre-defined instruction blocks, you ensure consistency and effectiveness in your AI-generated content. Follow these steps:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0' }}>1. Navigate the Wizard</Typography>
            <Typography variant="body2" color="text.secondary">
              Use the <strong>"Continue"</strong> and <strong>"Previous"</strong> buttons at the bottom to move through the different prompt categories (Writing Style, Introduction, Content Structure, etc.). The progress bar at the top shows where you are.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0' }}>2. Select Instruction Blocks</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              In each step, you'll see several options (blocks) for instructing the AI. Click on the block that best fits your needs for that category.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Important:</strong> You can typically select only <strong>one block per specific type</strong> (e.g., one Tone, one Introduction Style). Clicking a different block will automatically replace your previous selection for that type. Selected blocks are highlighted and show a checkmark.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0' }}>3. Review Your Choices</Typography>
            <Typography variant="body2" color="text.secondary">
              As you select blocks, they appear in the <strong>"Selected Options"</strong> bar near the top. This helps you keep track of the instructions you've chosen so far. You can remove a block by clicking the small 'X' icon on its pill in this bar.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0' }}>4. Add Custom Requirements (Final Step)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              On the "Review & Generate" step, you have the option to add <strong>Custom Requirements</strong>. Use this text box for any specific instructions not covered by the blocks.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Examples: "Mention the 2024 marketing trends report.", "Target audience is small business owners.", "Keep the tone slightly humorous.", "Do not mention competitor X."
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0' }}>5. Generate Your Prompt</Typography>
            <Typography variant="body2" color="text.secondary">
              Once you're satisfied with your selections and custom requirements, click the <strong>"Generate Prompt"</strong> button. The AI will analyze your choices and combine them into a comprehensive, ready-to-use prompt.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0' }}>6. Review & Save</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A dialog will pop up showing the final prompt text. You can <strong>review and even edit</strong> the generated prompt directly in the text area.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              To save this prompt for future use, give it a descriptive <strong>Name</strong> (e.g., "Professional Blog Post - SEO Focused") and click <strong>"Save Prompt"</strong>. Saved prompts appear in the Prompt Lab.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you just want to use the prompt immediately without saving, you can click <strong>"Use Without Saving"</strong> (you might want to copy the text first!).
            </Typography>
          </Box>

          <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#4158D0', 0.05), borderRadius: '8px' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#4158D0', fontSize: '1rem' }}>Tips for Success</Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0, mb: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Combine different block types to create unique instructions.</Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Be specific in the "Custom Requirements" field for nuances the blocks don't cover.</Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Experiment! Try different block combinations to see how they affect the AI output.</Typography>
              <Typography component="li" variant="body2" color="text.secondary">Save prompts you like with clear names so you can easily reuse them later.</Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: '8px', px: 3 }}>Got it</Button>
      </DialogActions>
    </Dialog>
  );
};

function PromptBuilder({ config }) {
  const { authenticatedRequest, error: authError, isAuthenticated, loading: authLoading } = useAuth();
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  
  const getAuxiliaryModel = () => {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return availableModels[0]?.value;
  };
  const [promptName, setPromptName] = useState('');
  const [customRequirements, setCustomRequirements] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    severity: 'success',
    message: ''
  });

  // Add a new state to track the wizard step
  const [activeStep, setActiveStep] = useState(0);

  // ---> ADD MISSING STATE HERE
  const [writingStyleMode, setWritingStyleMode] = useState('blocks'); // 'blocks' or 'persona'
  const [selectedPersona, setSelectedPersona] = useState('');
  const PERSONA_OPTIONS = [
    'Car Mechanic',
    'Maths Tutor',
    'Travel Blogger',
    'Historian',
    'Tech Reviewer',
    'Healthcare Professional',
    'Financial Analyst',
    'Chef',
    'Personal Trainer',
    'Marketing Manager',
    'Software Engineer',
    'Science Teacher',
    'DIY Handyman',
    'Legal Advisor'
  ];
  // <--- END ADDED STATE

  // Define the wizard steps
  const wizardSteps = [
    { label: "Writing Style", key: "writingStyle", description: "Choose how your content should sound" },
    { label: "Introduction", key: "introduction", description: "Select how to begin your content" },
    { label: "Content Structure", key: "contentStructure", description: "Define the overall structure" },
    { label: "SEO Features", key: "seoFeatures", description: "Optimize for search engines" },
    { label: "Additional Elements", key: "additionalElements", description: "Add extra components" },
    { label: "Conclusion", key: "conclusion", description: "Choose how to end your content" },
    { label: "Review & Generate", key: "review", description: "Review your selections and generate your prompt" }
  ];

  useEffect(() => {
    // Initialize all sections as collapsed
    const sections = Object.keys(PROMPT_BLOCKS).reduce((acc, key) => {
      acc[key] = key === 'writingStyle';
      return acc;
    }, {});
    setExpandedSections(sections);
    
    // Fetch available models
    const fetchModels = async () => {
      try {
        const models = await authenticatedRequest(() => 
          apiClient.getAvailableModels()
        );
        setAvailableModels(models);
        if (models.length > 0) {
          setSelectedModel(models[0].value);
        }
      } catch (error) {
        console.error('Error fetching models:', error);
        setError('Failed to load available models');
      }
    };

    fetchModels();
  }, [authenticatedRequest]);

  // Handle step navigation
  const handleNext = () => {
    setActiveStep((prevStep) => Math.min(prevStep + 1, wizardSteps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  // Update the expandedSections state when step changes
  useEffect(() => {
    if (activeStep < wizardSteps.length - 1) { // If not at the review step
      const newExpandedSections = Object.keys(PROMPT_BLOCKS).reduce((acc, key) => {
        acc[key] = key === wizardSteps[activeStep].key;
        return acc;
      }, {});
      setExpandedSections(newExpandedSections);
    }
  }, [activeStep]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    if (destination.droppableId === 'selected-blocks') {
      if (source.droppableId === 'selected-blocks') {
        // Reordering within selected blocks
        const newBlocks = Array.from(selectedBlocks);
        const [movedBlock] = newBlocks.splice(source.index, 1);
        newBlocks.splice(destination.index, 0, movedBlock);
        setSelectedBlocks(newBlocks);
      } else {
        // Adding from available blocks
        const sourceCategory = source.droppableId;
        const blockToAdd = PROMPT_BLOCKS[sourceCategory].blocks[source.index];
        
        // Check if block already exists
        const blockExists = selectedBlocks.some(block => block.id === blockToAdd.id);
        
        if (!blockExists) {
          const newBlock = {
            ...blockToAdd,
            uniqueId: `${blockToAdd.id}-${Date.now()}`
          };
          
          const newBlocks = Array.from(selectedBlocks);
          newBlocks.splice(destination.index, 0, newBlock);
          setSelectedBlocks(newBlocks);
        }
      }
    }
  };

  const handleAddBlock = useCallback((blockToAdd) => {
    const filteredBlocks = selectedBlocks.filter(
      block => block.category !== blockToAdd.category
    );
    
    const newBlock = {
      ...blockToAdd,
      uniqueId: `${blockToAdd.id}-${Date.now()}`
    };
    
    setSelectedBlocks([...filteredBlocks, newBlock]);
  }, [selectedBlocks]);

  const [promptDialogOpen, setPromptDialogOpen] = useState(false);

  // Modify generatePromptTemplate to handle persona mode
  const generatePromptTemplate = (selectedBlocks, customRequirements) => {
    const organizedBlocks = selectedBlocks.reduce((acc, block) => {
      // No need to exclude tone/language here, they won't exist if persona is selected
      acc[block.category] = block;
      return acc;
    }, {});

    let promptText = `Please write an article using the following guidelines:\n\n`;
    let instructionNumber = 1;

    // Check if a persona block exists
    const personaBlock = selectedBlocks.find(b => b.category === 'persona');
    if (personaBlock) {
      // Use the template from the persona block
      promptText += `${instructionNumber}. ${personaBlock.template}\n`;
      instructionNumber++;
    }

    // Add other selected block templates
    Object.values(organizedBlocks).forEach(block => {
      // Skip the persona block if it was already added
      if (block.category !== 'persona') {
        promptText += `${instructionNumber}. ${block.template}\n`;
        instructionNumber++;
      }
    });

    if (customRequirements.trim()) {
      promptText += `\nAdditional Requirements:\n${customRequirements.trim()}\n`;
    }

    promptText += `\nPlease ensure the content flows naturally and reads as if written by a human expert rather than an AI.`;

    return promptText;
  };

  const handleGeneratePrompt = async () => {
    // Updated validation: Check for persona OR tone/language blocks
    const hasStyleSelection = selectedBlocks.some(b =>
      b.category === 'persona' || b.category === 'tone' || b.category === 'language'
    );

    if (!hasStyleSelection && selectedBlocks.length === 0) {
       setError('Please select at least one option or choose a persona to generate a prompt');
       return;
    }

    setLoading(true);
    setError(null);

    try {
      const auxModel = getAuxiliaryModel();
      if (!auxModel) {
        throw new Error('No AI models available');
      }

      // Generate locally first
      const localTemplate = generatePromptTemplate(
        selectedBlocks,
        customRequirements
      );

      try {
        // Try to get AI-enhanced version
        const apiRequest = {
          model: auxModel,
          settings: {
            blocks: selectedBlocks,
            customRequirements: customRequirements.trim(),
          }
        };
  
        const response = await authenticatedRequest(() => 
          apiClient.generatePrompt(apiRequest)
        );
        
        // Use API response if available, otherwise use local template
        setGeneratedPrompt(response.prompt || localTemplate);
      } catch (err) {
        console.error('API prompt generation failed, using local template', err);
        setGeneratedPrompt(localTemplate);
      }
      
      // Show the dialog with the prompt
      setPromptDialogOpen(true);
      
    } catch (err) {
      console.error('Prompt generation error:', err);
      setError('Failed to generate prompt: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSavePrompt = async () => {
    if (!promptName.trim()) {
      setError('Please enter a name for your prompt');
      return;
    }
  
    try {
      await authenticatedRequest(() => 
        apiClient.createPrompt({
          name: promptName,
          prompt_text: generatedPrompt,
          settings: {
            blocks: selectedBlocks,
            customRequirements,
            template: generatePromptTemplate(selectedBlocks, customRequirements)
          }
        })
      );
  
      setPromptName('');
      setNotification({
        open: true,
        severity: 'success',
        message: 'Prompt template saved successfully!'
      });
    } catch (error) {
      setNotification({
        open: true,
        severity: 'error',
        message: `Save failed: ${error.message}`
      });
    }
  };
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (authLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          Please log in to use this feature
        </Alert>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {loading && <LoadingOverlay />}
      <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 4 } }}>
        {authError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {authError}
          </Alert>
        )}

        {/* Page Header */}
        <PageHeader
          category="Prompt Lab"
          title="Guided Prompt Builder"
          icon={
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #6A7BFF 0%, #4158D0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(65, 88, 208, 0.2)',
            }}>
              <Blocks size={24} color="#FFFFFF" strokeWidth={1.5} />
            </Box>
          }
          description="Create powerful prompts in minutes with our easy step-by-step interface."
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            onClick={() => setHelpDialogOpen(true)}
            startIcon={<HelpCircle size={18} />}
            sx={{
              bgcolor: alpha('#4158D0', 0.1),
              color: '#4158D0',
              borderRadius: '8px',
              px: 2,
              py: 1,
              '&:hover': {
                bgcolor: alpha('#4158D0', 0.2)
              }
            }}
          >
            How to Use
          </Button>
        </Box>

        {/* Main Content Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: '16px', 
            border: '1px solid',
            borderColor: alpha('#4158D0', 0.1),
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)'
          }}
        >
          {/* Simplified Progress Indicator */}
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              px: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha('#f8f9fc', 0.6)
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1A2034',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Step {activeStep + 1} of {wizardSteps.length}:
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  ml: 1,
                  color: '#4158D0',
                  fontWeight: 600
                }}
              >
                {wizardSteps[activeStep].label}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress:
              </Typography>
              <Box sx={{ 
                position: 'relative', 
                width: { xs: 60, sm: 100 }, 
                height: 6, 
                bgcolor: alpha('#4158D0', 0.1), 
                borderRadius: 3 
              }}>
                <Box 
                  sx={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${((activeStep + 1) / wizardSteps.length) * 100}%`,
                    background: 'linear-gradient(90deg, #4158D0, #6A7BFF)',
                    borderRadius: 3,
                    transition: 'width 0.3s ease'
                  }} 
                />
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#4158D0',
                  minWidth: 35,
                  textAlign: 'right'
                }}
              >
                {Math.round(((activeStep + 1) / wizardSteps.length) * 100)}%
              </Typography>
            </Box>
          </Box>

          {/* Step Description */}
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {wizardSteps[activeStep].description}
              <Tooltip 
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {activeStep < wizardSteps.length - 1 
                        ? "Select one option from this category to build your prompt. Each selection shapes how your final content will be created."
                        : "Review all your selections and add any custom requirements before generating your prompt."
                      }
                    </Typography>
                    {activeStep < wizardSteps.length - 1 && (
                      <Typography variant="body2">
                        You can change your selection anytime by clicking a different option.
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="bottom-start"
              >
                <IconButton size="small" sx={{ ml: 1, color: alpha('#4158D0', 0.7) }}>
                  <Info size={16} />
                </IconButton>
              </Tooltip>
            </Typography>
          </Box>

          {/* Selected Options Pill Bar */}
          {selectedBlocks.length > 0 && (
            <Box 
              sx={{ 
                px: 3, 
                py: 2, 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                bgcolor: alpha('#f8f9fc', 0.3)
              }}
            >
              {selectedBlocks.map((selectedBlock) => {
                // Find which category this block belongs to
                const categoryKey = Object.keys(PROMPT_BLOCKS).find(key => 
                  PROMPT_BLOCKS[key].blocks.some(block => block.category === selectedBlock.category)
                );
                
                if (!categoryKey) return null;
                
                const IconComponent = PROMPT_BLOCKS[categoryKey].icon;
                
                return (
                  <Box
                    key={selectedBlock.uniqueId || selectedBlock.id}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      borderRadius: '20px',
                      bgcolor: alpha('#4158D0', 0.08),
                      border: '1px solid',
                      borderColor: alpha('#4158D0', 0.1),
                      maxWidth: { xs: '100%', sm: 250 },
                    }}
                  >
                    {IconComponent && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#4158D0',
                          flexShrink: 0
                        }}
                      >
                        <IconComponent size={14} />
                      </Box>
                    )}
                    <Typography 
                      variant="body2" 
                      noWrap 
                      sx={{ 
                        fontWeight: 500,
                        color: '#4158D0', 
                        flexGrow: 1,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {selectedBlock.label}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newBlocks = selectedBlocks.filter(
                          b => b.id !== selectedBlock.id
                        );
                        setSelectedBlocks(newBlocks);
                      }}
                      sx={{ 
                        p: 0.25,
                        color: alpha('#4158D0', 0.6),
                        '&:hover': {
                          bgcolor: alpha('#4158D0', 0.1),
                          color: '#4158D0'
                        }
                      }}
                    >
                      <XCircle size={14} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Main Content Area */}
          <Box sx={{ p: 3 }}>
            {activeStep < wizardSteps.length - 1 ? (
              // Regular Steps - Options selection
              <Box>
                {activeStep === 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Tabs
                      value={writingStyleMode}
                      onChange={(event, newValue) => {
                        setWritingStyleMode(newValue);
                        // Clear the other mode's selections when switching
                        if (newValue === 'persona') {
                          // Remove tone/language blocks
                          setSelectedBlocks(prev => prev.filter(b => b.category !== 'tone' && b.category !== 'language'));
                        } else {
                          // Remove persona block
                          setSelectedBlocks(prev => prev.filter(b => b.category !== 'persona'));
                          setSelectedPersona(''); // Also clear persona state if kept
                        }
                      }}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                    >
                      <Tab label="Style Blocks" value="blocks" />
                      <Tab label="Select Persona" value="persona" />
                    </Tabs>

                    {writingStyleMode === 'persona' && (
                      <FormControl fullWidth>
                        <InputLabel id="persona-select-label">Choose a Persona</InputLabel>
                        <Select
                          labelId="persona-select-label"
                          id="persona-select"
                          value={selectedPersona}
                          label="Choose a Persona"
                          onChange={(e) => {
                            const personaValue = e.target.value;
                            setSelectedPersona(personaValue); // Keep updating state for dropdown
                            
                            // Remove existing style/persona blocks
                            let updatedBlocks = selectedBlocks.filter(
                              b => b.category !== 'tone' && b.category !== 'language' && b.category !== 'persona'
                            );

                            // Add new persona block if a value is selected
                            if (personaValue) {
                              updatedBlocks.push({
                                id: `persona-${personaValue.replace(/\s+/g, '-').toLowerCase()}`,
                                uniqueId: `persona-${Date.now()}`,
                                category: 'persona',
                                label: `Persona: ${personaValue}`,
                                template: `Write in the style of a ${personaValue}.`
                              });
                            }
                            setSelectedBlocks(updatedBlocks);
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {PERSONA_OPTIONS.map((persona) => (
                            <MenuItem key={persona} value={persona}>
                              {persona}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                )}
                {(writingStyleMode === 'blocks' || activeStep !== 0) && PROMPT_BLOCKS[wizardSteps[activeStep].key].blocks.map((block, index) => {
                  const isSelected = selectedBlocks.some(selected => selected.id === block.id);
                  const isDisabled = selectedBlocks.some(
                    selected => selected.category === block.category && selected.id !== block.id
                  );
                  // Persona blocks are handled separately
                  if (block.category === 'persona') return null;

                  // Prevent rendering tone/language blocks if a persona is active (by checking selectedBlocks)
                  const hasPersonaBlock = selectedBlocks.some(b => b.category === 'persona');
                  if (activeStep === 0 && hasPersonaBlock && (block.category === 'tone' || block.category === 'language')) {
                    return null;
                  }

                  return (
                    <Paper
                      key={block.id}
                      elevation={0}
                      onClick={() => {
                        // Prevent interaction if disabled
                        if (isDisabled) return;

                        // Cannot select tone/language if persona is active
                        if (activeStep === 0 && hasPersonaBlock && (block.category === 'tone' || block.category === 'language')) {
                           return;
                        }

                        if (isSelected) {
                          // Remove the block if already selected (unselect)
                          setSelectedBlocks(selectedBlocks.filter(
                            b => b.id !== block.id
                          ));
                        } else {
                           // If selecting a tone/language block, remove persona block first
                           let blocksWithoutPersona = selectedBlocks;
                           if (activeStep === 0 && (block.category === 'tone' || block.category === 'language')) {
                              blocksWithoutPersona = selectedBlocks.filter(b => b.category !== 'persona');
                              setSelectedPersona(''); // Clear persona state
                           }
                           // Filter out any other block in the same category
                           const filteredBlocks = blocksWithoutPersona.filter(
                              b => b.category !== block.category
                           );
                           // Add the new block
                           const newBlock = { ...block, uniqueId: `${block.id}-${Date.now()}` };
                           setSelectedBlocks([...filteredBlocks, newBlock]);
                        }
                      }}
                      sx={{
                        p: 2.5,
                        mb: 2,
                        border: '1.5px solid',
                        borderColor: isSelected
                          ? '#4158D0'
                          : isDisabled
                            ? alpha('#e0e0e0', 0.8)
                            : alpha('#cfd7f9', 0.5),
                        borderRadius: '12px',
                        opacity: isDisabled ? 0.5 : 1,
                        position: 'relative',
                        transition: 'all 0.15s ease',
                        // Slightly adjusted cursor logic
                        cursor: isDisabled || (activeStep === 0 && hasPersonaBlock && (block.category === 'tone' || block.category === 'language')) ? 'default' : 'pointer',
                        bgcolor: isSelected ? alpha('#4158D0', 0.04) : '#ffffff',
                        boxShadow: isSelected
                          ? `0 2px 8px ${alpha('#4158D0', 0.15)}`
                          : isDisabled
                            ? 'none'
                            : `0 1px 3px ${alpha('#000', 0.05)}`,
                        '&:hover': {
                           // Adjusted hover logic
                           borderColor: !isDisabled && !(activeStep === 0 && hasPersonaBlock && (block.category === 'tone' || block.category === 'language')) ? '#4158D0' : (isSelected ? '#4158D0' : (isDisabled ? alpha('#e0e0e0', 0.8) : alpha('#cfd7f9', 0.5))),
                           boxShadow: !isDisabled && !(activeStep === 0 && hasPersonaBlock && (block.category === 'tone' || block.category === 'language')) ? `0 3px 10px ${alpha('#4158D0', 0.2)}` : (isSelected ? `0 2px 8px ${alpha('#4158D0', 0.15)}` : 'none'),
                           transform: !isDisabled && !(activeStep === 0 && hasPersonaBlock && (block.category === 'tone' || block.category === 'language')) ? 'translateY(-1px)' : 'none'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ flex: 1, pr: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              color: isSelected ? '#4158D0' : '#1A2034'
                            }}
                          >
                            {block.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {block.template}
                          </Typography>
                        </Box>

                        {isSelected ? (
                          <Box
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              borderRadius: '50%', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              bgcolor: '#4158D0',
                              color: 'white',
                              boxShadow: `0 2px 5px ${alpha('#4158D0', 0.3)}`
                            }}
                          >
                            <Check size={18} />
                          </Box>
                        ) : (
                          !isDisabled && (
                            <Box 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                borderRadius: '50%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                bgcolor: alpha('#4158D0', 0.1),
                                color: '#4158D0',
                              }}
                            >
                              <PlusCircle size={18} />
                            </Box>
                          )
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              // Review Step - Show custom requirements
              <Box>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1A2034',
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Sparkles size={18} style={{ marginRight: '8px', color: '#4158D0' }} />
                  Custom Requirements (Optional)
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    border: '1.5px solid',
                    borderColor: alpha('#cfd7f9', 0.5),
                    borderRadius: '12px',
                    boxShadow: `0 1px 3px ${alpha('#000', 0.05)}`
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Add any specific instructions or requirements not covered by your selections above..."
                    value={customRequirements}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (input.length > 500) {
                        setError('Input exceeds maximum length of 500 characters');
                        return;
                      }
                      if (!isValidInput(input)) {
                        setError('Invalid input detected. Please remove any special characters or code.');
                        return;
                      }
                      setError(null);
                      setCustomRequirements(sanitizeInput(input));
                    }}
                    helperText={`${customRequirements.length}/500 characters`}
                    InputProps={{
                      sx: {
                        bgcolor: 'transparent',
                        border: 'none',
                        '& .MuiOutlinedInput-notchedOutline': { 
                          border: 'none' 
                        }
                      }
                    }}
                  />
                </Paper>
                
                {error && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                  </Alert>
                )}
                
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    bgcolor: alpha('#f8f9fc', 0.7),
                    p: 2,
                    borderRadius: '8px'
                  }}
                >
                  <Info size={16} style={{ marginRight: '8px', color: alpha('#4158D0', 0.8) }} />
                  {(() => {
                    // Get count of categories that have at least one selection
                    const selectedCategories = Object.keys(PROMPT_BLOCKS).filter(category => 
                      selectedBlocks.some(block => 
                        PROMPT_BLOCKS[category].blocks.some(categoryBlock => 
                          categoryBlock.id === block.id
                        )
                      )
                    );
                    return `You've made selections from ${selectedCategories.length} of ${Object.keys(PROMPT_BLOCKS).length} possible categories.`;
                  })()}
                  Click "Generate Prompt" when you're ready to create your prompt.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Navigation Buttons */}
          <Box 
            sx={{ 
              p: 3, 
              borderTop: '1px solid', 
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              bgcolor: alpha('#f8f9fc', 0.6)
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowLeft size={16} />}
              sx={{ 
                borderRadius: '8px',
                borderColor: alpha('#4158D0', 0.3),
                color: '#4158D0',
                px: 2
              }}
            >
              Previous
            </Button>
            
            {activeStep === wizardSteps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGeneratePrompt}
                disabled={loading || selectedBlocks.length === 0}
                endIcon={loading ? null : <Sparkles size={16} />}
                sx={{ 
                  borderRadius: '8px',
                  background: 'linear-gradient(90deg, #4158D0, #6A7BFF)',
                  px: 3,
                  boxShadow: `0 4px 10px ${alpha('#4158D0', 0.3)}`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {loading ? (
                  <>
                    <Box sx={{ 
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0.1,
                      backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                      animation: 'shimmer 1.5s infinite',
                      '@keyframes shimmer': {
                        '0%': { transform: 'translateX(-100%)' },
                        '100%': { transform: 'translateX(100%)' }
                      }
                    }} />
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : 'Generate Prompt'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowRight size={16} />}
                sx={{ 
                  borderRadius: '8px',
                  background: 'linear-gradient(90deg, #4158D0, #6A7BFF)',
                  px: 3,
                  boxShadow: `0 4px 10px ${alpha('#4158D0', 0.3)}`
                }}
              >
                Continue
              </Button>
            )}
          </Box>
        </Paper>

        {/* Result and Save Dialog */}
        <Dialog 
          open={promptDialogOpen} 
          onClose={() => setPromptDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 10px 35px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden'
            }
          }}
        >
          {/* Header with gradient background */}
          <Box sx={{ 
            p: 3,
            background: 'linear-gradient(90deg, #4158D0, #6A7BFF)',
            color: 'white'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                color: 'white'
              }}
            >
              <Sparkles size={20} />
              Your Prompt is Ready
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              Generated from {selectedBlocks.length} building blocks
            </Typography>
          </Box>

          <DialogContent sx={{ p: 0 }}>
            {/* Success message with instructions */}
            <Box sx={{ 
              p: 3,
              backgroundColor: alpha('#4caf50', 0.08),
              borderBottom: '1px solid',
              borderColor: alpha('#4caf50', 0.2),
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2
            }}>
              <Check size={20} color="#4caf50" style={{ marginTop: 2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                  Your prompt has been generated successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and edit the prompt below if needed. Add a name to save it to your prompt library.
                </Typography>
              </Box>
            </Box>

            {/* Prompt text section */}
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A2034' }}>
                    Prompt Text
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        navigator.clipboard.writeText(generatedPrompt);
                        setNotification({
                          open: true,
                          severity: 'success',
                          message: 'Prompt copied to clipboard'
                        });
                      }}
                      sx={{ 
                        color: '#4158D0',
                        '&:hover': {
                          backgroundColor: alpha('#4158D0', 0.1)
                        }
                      }}
                    >
                      <Copy size={16} />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                <Paper
                  elevation={0}
                  sx={{
                    p: 0,
                    border: '1px solid',
                    borderColor: alpha('#4158D0', 0.2),
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    value={generatedPrompt}
                    onChange={(e) => setGeneratedPrompt(e.target.value)}
                    InputProps={{
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        bgcolor: alpha('#f5f7fa', 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        p: 2
                      }
                    }}
                  />
                </Paper>
              </Box>

              {/* Prompt name section with enhanced styling */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A2034', mb: 1 }}>
                  Name Your Prompt
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Give your prompt a descriptive name so you can easily find it later.
                </Typography>
                
                <Paper
                  elevation={0}
                  sx={{
                    p: 0,
                    border: '1px solid',
                    borderColor: alpha('#4158D0', 0.2),
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <TextField
                    placeholder="E.g., Professional Blog Post Template"
                    value={promptName}
                    onChange={(e) => setPromptName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        p: 1
                      },
                      startAdornment: (
                        <Box sx={{ 
                          mr: 1, 
                          ml: 1,
                          display: 'flex',
                          alignItems: 'center',
                          color: alpha('#4158D0', 0.7)
                        }}>
                          <PenTool size={18} />
                        </Box>
                      )
                    }}
                  />
                </Paper>
                
                {!promptName && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: alpha('#f44336', 0.7) }}>
                    * A name is required to save this prompt
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          
          <Box 
            sx={{ 
              px: 3, 
              py: 2.5, 
              bgcolor: alpha('#f8f9fc', 0.7),
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Button 
              onClick={() => setPromptDialogOpen(false)}
              variant="outlined"
              startIcon={<X size={16} />}
              sx={{ 
                borderRadius: '8px',
                borderColor: alpha('#000', 0.2),
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.05),
                  borderColor: alpha('#000', 0.3),
                }
              }}
            >
              Close
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={() => {
                  // Just close the dialog without saving
                  setPromptDialogOpen(false);
                }}
                variant="outlined"
                sx={{ 
                  borderRadius: '8px',
                  borderColor: alpha('#4158D0', 0.3),
                  color: '#4158D0',
                  '&:hover': {
                    backgroundColor: alpha('#4158D0', 0.05),
                    borderColor: '#4158D0',
                  }
                }}
              >
                Use Without Saving
              </Button>
              
              <Button 
                variant="contained"
                onClick={() => {
                  handleSavePrompt();
                  setPromptDialogOpen(false);
                }}
                disabled={!promptName.trim()}
                startIcon={<Sparkles size={16} />}
                sx={{ 
                  borderRadius: '8px',
                  background: 'linear-gradient(90deg, #4158D0, #6A7BFF)',
                  boxShadow: `0 4px 10px ${alpha('#4158D0', 0.3)}`,
                  px: 3,
                  '&:hover': {
                    boxShadow: `0 6px 15px ${alpha('#4158D0', 0.4)}`,
                  },
                  '&.Mui-disabled': {
                    background: alpha('#4158D0', 0.3),
                    color: alpha('#fff', 0.7)
                  }
                }}
              >
                Save Prompt
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Notification */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            sx={{ 
              width: '100%',
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}
            action={
              notification.severity === 'success' && (
                <Button 
                  color="inherit" 
                  size="small" 
                  component={Link} 
                  to="/prompts/saved"
                >
                  View Saved
                </Button>
              )
            }
          >
            {notification.message}
          </Alert>
        </Snackbar>

        {/* Add the HelpDialog instance */}
        <HelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />

      </Container>
    </DragDropContext>
  );
}

export default React.memo(PromptBuilder);